/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ComponentType, useContext, useEffect, useState } from 'react';

import { FormattedMessage } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import Lozenge from '@atlaskit/lozenge';
import { Text } from '@atlaskit/primitives/compiled';

import type {
	InvitedAppShortcutAllowedApsKey,
	InvitedAppShortcutAllowedTargetAppKey,
	InvitedAppShortcutVariant,
} from '../../../../../common/types';
import { messages } from '../../../../../common/ui/invited-app-shortcuts/messages';
import { getAppKeyByApsKey } from '../../../../../common/utils/invited-app-shortcuts-utils';
import { InvitedAppShortcutsContext } from '../../../../../controllers/invited-app-shortcuts-context';
import { useInvitedAppAnalytics } from '../../../../../services/invited-app-shortcuts/invited-app-analytics';

interface InvitedAppShortcutLabelProps {
	apsKey: InvitedAppShortcutAllowedApsKey;
	label: string;
	/**
	 * Called once when the animated lozenge is impressed, so the caller can warm
	 * GRS to the static variant for the next load. Only the productionisation
	 * container supplies it; the legacy cohort has no GRS entry to refresh.
	 */
	onAnimatedImpressed?: (app: InvitedAppShortcutAllowedTargetAppKey) => void;
	/**
	 * Server-resolved lozenge treatment; authoritative when supplied.
	 *
	 * Absent is NOT `'none'`. This label is shared by both container modes and only
	 * the productionisation inner resolves a variant — the legacy inner's
	 * `resolveFromCache` hardcodes `'none'`, so reading absent as `'none'` would
	 * drop that cohort's lozenge. Absent means "use the legacy heuristic", and prop
	 * presence is what scopes this path: productionisation is already behind
	 * `twcg-460-preload-grs-invited-app-shortcuts`.
	 */
	variant?: InvitedAppShortcutVariant;
}

const styles = cssMap({
	container: {
		display: 'grid',
		gridTemplateColumns: 'auto 1fr',
		alignItems: 'center',
		width: '100%',
	},
	lozengeContainer: {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'center',
		minWidth: '0px',
	},
});

const INITIAL_ANIMATION_DELAY = 2000;

// apsKeys that have already played their initial animation. Module-scoped so it survives the
// frequent subtree remounts (a `useRef` would be discarded); keyed by apsKey so products stay
// independent; not React state so flipping it can't re-render and cut the animation short.
const impressedApsKeys = new Set<InvitedAppShortcutAllowedApsKey>();

export const InvitedAppShortcutLabel = ({
	label,
	apsKey,
	variant,
	onAnimatedImpressed,
}: InvitedAppShortcutLabelProps): JSX.Element => {
	const { impressedProducts } = useContext(InvitedAppShortcutsContext);
	const [AnimatedLozengeComponent, setAnimatedLozengeComponent] =
		useState<ComponentType<any> | null>(null);
	const { fireViewedAnimatedLozenge } = useInvitedAppAnalytics();

	// A stale `animated` (already played this session) degrades to static.
	const hasAlreadyAnimated = impressedApsKeys.has(apsKey);

	const resolveTreatment = (): InvitedAppShortcutVariant => {
		// Legacy cohort: first sighting animates, thereafter static.
		if (variant === undefined) {
			return impressedProducts[apsKey] || hasAlreadyAnimated ? 'static' : 'animated';
		}

		switch (variant) {
			case 'animated':
				return hasAlreadyAnimated ? 'static' : 'animated';
			case 'static':
				return 'static';
			// Keeps an unrecognised future variant from animating.
			default:
				return 'none';
		}
	};

	const treatment = resolveTreatment();
	const wantsAnimated = treatment === 'animated';

	useEffect(() => {
		// `static` and `none` skip the chunk, so they paint synchronously.
		if (!wantsAnimated) {
			return;
		}
		import(
			/* webpackChunkName: "@atlassian/invited-app-shortcut-animated-lozenge" */ './animated-lozenge/main'
		).then((module) => {
			setAnimatedLozengeComponent(module.default as ComponentType<any>);
		});
	}, [wantsAnimated]);

	useEffect(() => {
		if (AnimatedLozengeComponent) {
			const app = getAppKeyByApsKey(apsKey);
			fireViewedAnimatedLozenge({ app });
			// Safe to re-run: the refresher dedupes per app, so an unstable callback
			// identity costs nothing.
			onAnimatedImpressed?.(app);
		}
	}, [AnimatedLozengeComponent, fireViewedAnimatedLozenge, apsKey, onAnimatedImpressed]);

	useEffect(() => {
		// Only an animated render marks the key impressed.
		if (!wantsAnimated) {
			return;
		}
		const timeoutId = setTimeout(() => {
			impressedApsKeys.add(apsKey);
		}, INITIAL_ANIMATION_DELAY);

		return () => clearTimeout(timeoutId);
	}, [apsKey, wantsAnimated]);

	const LozengeComponent =
		wantsAnimated && AnimatedLozengeComponent ? AnimatedLozengeComponent : Lozenge;
	const lozengeProps =
		LozengeComponent === AnimatedLozengeComponent
			? { initialAnimationDelay: INITIAL_ANIMATION_DELAY }
			: {};

	const shouldRenderLozenge =
		treatment === 'static' || (wantsAnimated && AnimatedLozengeComponent !== null);

	return (
		<div css={styles.container}>
			<Text>{label}</Text>
			<div css={styles.lozengeContainer}>
				{shouldRenderLozenge ? (
					<LozengeComponent appearance="inprogress" {...lozengeProps}>
						<FormattedMessage {...messages.invitedLozenge} />
					</LozengeComponent>
				) : null}
			</div>
		</div>
	);
};
